const SCROLL_INTERVAL_MS = 700;
let receiveSignal = null;

// Called only for accepted signals, after the coordinator's self-request guard.
export const signalAuthorizationTitleAttention = event => {
  receiveSignal?.(event);
};

export const initializeAuthorizationTitleAttention = ({ store, router }) => {
  let baseTitle = document.title;
  let lastWrittenTitle = document.title;
  let hasMessage = false;
  let hasRequest = false;
  let hasResponse = false;
  let offset = 0;
  let timer = null;
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const writeTitle = title => {
    if (document.title !== title) document.title = title;
    // The browser strips/collapses ASCII whitespace in this getter. Remember
    // that actual value so our own mutation cannot trigger another rewrite.
    lastWrittenTitle = document.title;
  };

  const capturePageTitle = () => {
    if (document.title !== lastWrittenTitle) {
      baseTitle = document.title;
    }
  };

  const stopTimer = () => {
    if (timer !== null) window.clearInterval(timer);
    timer = null;
  };

  const reset = () => {
    capturePageTitle();
    stopTimer();
    hasMessage = false;
    hasRequest = false;
    hasResponse = false;
    offset = 0;
    writeTitle(baseTitle);
  };

  const isAttendingWorkspace = () => {
    if (document.visibilityState !== "visible" || !document.hasFocus()) return false;

    const path = router.currentRoute.value.path.replace(/\/$/, "");
    return (store.state.user?.type === "hmo" && path === "/auth")
      || (store.state.user?.type === "provider" && path === "/care/encounter");
  };

  const render = () => {
    const labels = [hasResponse && "New response", hasMessage && "New message", hasRequest && "New request"].filter(Boolean);
    const text = `${labels.join(" • ")} • `;
    const position = reducedMotion.matches ? 0 : offset % text.length;
    writeTitle(`LifeWORTH • ${text.slice(position)}${text.slice(0, position)}`);
  };

  const reconcile = () => {
    capturePageTitle();
    if (!store.state.user || isAttendingWorkspace()) {
      reset();
      return;
    }
    if (!hasMessage && !hasRequest && !hasResponse) return;

    render();
    if (reducedMotion.matches) {
      stopTimer();
    } else if (timer === null) {
      timer = window.setInterval(() => {
        offset += 1;
        reconcile();
      }, SCROLL_INTERVAL_MS);
    }
  };

  const receive = event => {
    const message = event?.action === "message_added" || Boolean(event?.has_message || event?.message_id);
    const request = event?.action === "requested";
    // The publisher emits "reviewed" for completed reviews; keep the documented
    // "review_completed" name compatible too.
    const response = store.state.user?.type === "provider"
      && ["reviewed", "review_completed", "review_partial"].includes(event?.action);
    if (!message && !request && !response) return;

    hasMessage = hasMessage || message;
    hasRequest = hasRequest || request;
    hasResponse = hasResponse || response;
    offset = 0;
    reconcile();
  };

  receiveSignal = receive;
  // Views still own their normal titles. Remember their writes while attention is active.
  const observer = new MutationObserver(() => {
    if (document.title !== lastWrittenTitle) reconcile();
  });
  observer.observe(document.head, { childList: true, subtree: true, characterData: true });
  const removeRouteHook = router.afterEach(reconcile);
  window.addEventListener("focus", reconcile);
  document.addEventListener("visibilitychange", reconcile);
  reducedMotion.addEventListener("change", reconcile);

  return {
    reset,
    stop() {
      observer.disconnect();
      removeRouteHook();
      window.removeEventListener("focus", reconcile);
      document.removeEventListener("visibilitychange", reconcile);
      reducedMotion.removeEventListener("change", reconcile);
      if (receiveSignal === receive) receiveSignal = null;
      reset();
    },
  };
};
